import React, { useEffect, useState } from 'react';
import { useAppData } from '../../../../../../context/AppDataContext';
import { useAlert } from '../../../../../../context/AlertContext';
import { NumberToWords } from './NumberToWords';
import { getGstBreakup } from '../gstHelper';
import voucherOutItemServices from '../voucherOutItemServices';
import cdNoteItemServices from '../cdNoteItemServices';
import { CircularProgress } from '@mui/material';
import { Save } from '@mui/icons-material';

/**
 * CDNoteItemList Component
 * 
 * AUDIT LOGIC: 
 * This component maintains a 1:1 link with the reference invoice. 
 * Items are never "removed" (deleted) to ensure they can be re-adjusted later.
 * Instead, a qty of 0 represents an item that is not being returned/adjusted.
 */
function CDNoteItemList({ voucherDetails, productList, isPrinting, companyDetails, callBack }) {
    const { getMasterLabel } = useAppData();
    const { AppAlert } = useAlert();

    // --- State ---
    const [itemList, setItemList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totals, setTotals] = useState({ subTotal: 0, gstTotal: 0, roundOff: 0, grandTotal: 0 });

    /**
     * Data Initialization:
     * Fetches items for the current Note. If empty, clones from the original reference.
     */
    const getItemList = () => {
        setLoading(true);
        voucherOutItemServices.getByVoucherId(voucherDetails.id).then(res => {
            if (res.data.length === 0 && voucherDetails.status === 1 && voucherDetails.referenceId) {
                pullItemsFromOriginal();
            } else {
                setItemList(res.data);
            }
        }).catch(err => console.error("Fetch Error:", err)).finally(() => setLoading(false));
    }

    const pullItemsFromOriginal = () => {
        voucherOutItemServices.getByVoucherId(voucherDetails.referenceId).then(res => {
            const clonedItems = res.data.map(item => {
                const { id, created_at, updated_at, voucherId, ...cleanItem } = item;
                return { 
                    ...cleanItem, 
                    voucherId: voucherDetails.id,
                    originalQty: item.qty, // Reference for validation
                    qty: 0, // Default to 0 so the user must explicitly enter return qty
                    amount: 0,
                    gstAmount: 0,
                    totalAmount: 0,
                    isNew: true 
                };
            });
            setItemList(clonedItems);
        });
    }

    useEffect(() => { getItemList(); }, [voucherDetails.id]);

    /**
     * Financial Engine:
     * Calculates all totals whenever the item list is modified.
     */
    useEffect(() => {
        let _subTotal = 0; let _gstTotal = 0;
        itemList.forEach(item => {
            _subTotal += Number(item.amount || 0);
            _gstTotal += Number(item.gstAmount || 0);
        });
        const rawTotal = _subTotal + _gstTotal;
        const roundedTotal = Math.round(rawTotal);
        
        setTotals({
            subTotal: _subTotal.toFixed(2),
            gstTotal: _gstTotal.toFixed(2),
            roundOff: (roundedTotal - rawTotal).toFixed(2),
            grandTotal: roundedTotal
        });
    }, [itemList]);

    /**
     * Qty Change Logic:
     * Prevents return quantity from exceeding the original sale/purchase.
     */
    const handleQtyChange = (index, value) => {
        const updatedList = [...itemList];
        const item = updatedList[index];
        
        // Handle empty input as 0 to keep calculations stable
        const newQty = value === "" ? 0 : Number(value);

        if (item.originalQty && newQty > item.originalQty) {
            AppAlert(`Max allowed: ${item.originalQty}`, "error");
            return;
        }

        item.qty = newQty;
        item.amount = Number(newQty * item.price);
        item.gstAmount = Number((item.amount * item.gstRate) / 100);
        item.totalAmount = Number(item.amount + item.gstAmount);
        
        setItemList(updatedList);
    };

    /**
     * Save Logic:
     * Persists all rows (including 0 qty) to the database.
     */
    const saveChanges = () => {
        setLoading(true);
        cdNoteItemServices.save({items:itemList})
            .then(() => {
                AppAlert("Voucher items updated");
                getItemList(); 
                if(callBack) callBack(); 
            })
            .catch((error) => AppAlert(error.msg || "Save Error", "error"))
            .finally(() => setLoading(false));
    };

    const taxRows = getGstBreakup(totals.gstTotal, companyDetails?.state, voucherDetails?.billingAddress?.supplyState);

    return (
        <div className='table-responsive'>
            {loading && <div className='text-center my-2'><CircularProgress size={20}/></div>}
            
            <table className='table table-sm table-bordered mb-0' style={{ fontSize: 12 }}>
                <thead className='table-light align-middle text-uppercase fw-bold'>
                    <tr>
                        <th className='text-center' width="40">#</th>
                        <th>Description</th>
                        <th className='text-center'>HSN</th>
                        <th className='text-center'>Rate</th>
                        <th className='text-center' width="130">Return Qty</th>
                        <th className='text-center'>UOM</th>
                        <th className='text-center'>Tax %</th>
                        <th className='text-end'>Taxable Amt</th>
                    </tr>
                </thead>
                <tbody className='align-middle'>
                    {itemList.map((item, i) => {
                        const isZero = Number(item.qty) === 0;
                        return (
                            <tr key={i} className={isZero && !isPrinting ? 'table-light text-muted' : ''}>
                                <td className='text-center'>{i + 1}</td>
                                <td>
                                    <div className='fw-bold'>{item.title}</div>
                                    {isZero && !isPrinting && (
                                        <span className='badge bg-white text-muted border' style={{fontSize: '9px'}}>Excluded</span>
                                    )}
                                </td>
                                <td className='text-center'>{item.hsn}</td>
                                <td className='text-center'>{item.price}</td>
                                <td>
                                    {voucherDetails.status === 1 && !isPrinting ? (
                                        <div className="px-1">
                                            <input 
                                                type="number" 
                                                className={`form-control form-control-sm text-center shadow-none fw-bold ${isZero ? 'text-muted' : 'text-primary'}`} 
                                                value={item.qty} 
                                                min="0"
                                                onFocus={(e) => e.target.select()}
                                                onChange={(e) => handleQtyChange(i, e.target.value)}
                                            />
                                        </div>
                                    ) : (
                                        <div className='text-center fw-bold'>{item.qty}</div>
                                    )}
                                    {item.originalQty && (
                                        <div style={{fontSize: 9}} className='text-muted text-center mt-1'>
                                            Inv Qty: {item.originalQty}
                                        </div>
                                    )}
                                </td>
                                <td className='text-center'>{getMasterLabel("product-uom", item.uom)}</td>
                                <td className='text-center'>{item.gstRate}%</td>
                                <td className='text-end fw-bold'>{Number(item.amount).toFixed(2)}</td>
                            </tr>
                        );
                    })}
                    
                    {!isPrinting && voucherDetails.status === 1 && itemList.length > 0 && (
                        <tr>
                            <td colSpan={8} className='text-end bg-light p-2'>
                                <button className='btn btn-sm btn-success px-4 shadow-sm' onClick={saveChanges} disabled={loading}>
                                    <Save fontSize='small' className='me-2'/> Save Adjustments
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr>
                        <th colSpan={7} className='text-end text-muted fw-normal'>Sub Total</th>
                        <th className='text-end'>{totals.subTotal}</th>
                    </tr>
                    {taxRows.map((row, idx) => (
                        <tr key={idx} className='border-0'>
                            <th colSpan={7} className='text-end fst-italic fw-normal text-muted'>{row.label}</th>
                            <th className='text-end fw-normal'>{row.amount}</th>
                        </tr>
                    ))}
                    {Number(totals.roundOff) !== 0 && (
                        <tr>
                            <th colSpan={7} className='text-end fst-italic fw-normal text-muted'>Round Off</th>
                            <th className='text-end fw-normal'>{totals.roundOff}</th>
                        </tr>
                    )}
                    <tr className='table-dark'>
                        <th colSpan={7} className='text-end uppercase'>Grand Total</th>
                        <th className='text-end' style={{ fontSize: 14 }}>
                            ₹ {Number(totals.grandTotal).toLocaleString('en-IN')}
                        </th>
                    </tr>
                    <tr>
                        <td colSpan={8} className='bg-light p-3'>
                            <div className='text-muted small text-uppercase mb-1'>Amount in words</div>
                            <strong className='text-dark'>
                                <NumberToWords amount={totals.grandTotal} /> Only
                            </strong>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

export default CDNoteItemList;